import { Injectable } from '@angular/core';
import { ImmutableContext } from '@ngxs-labs/immer-adapter';
import {
   Action,
   ActionCompletion,
   Actions,
   ofActionCompleted,
   ofActionDispatched,
   ofActionSuccessful,
   Selector,
   State,
   StateContext,
   Store,
} from '@ngxs/store';
import { GridDataResult } from '@progress/kendo-angular-grid';
import _ from 'lodash';
import { StateResetAll } from 'ngxs-reset-plugin';
import { interval, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, groupBy, map, mergeMap, switchMap, takeUntil, takeWhile as takewhile, tap } from 'rxjs/operators';
import { SetUniqueCallIdForHistory } from 'src/app/store/samwin/call-tags/call-tags.actions';
import { StartNewCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { SessionState } from 'src/app/store/session/session.state';
import {
   CallHistoryEntryRemoteDto,
   CallHistoryFilterRemoteDto,
   CallHistoryProgressMemoInfoRemoteDto,
   CallHistoryResponseData,
   CallHistoryService,
   SamwinUserShortInfoRemoteDto,
} from '../../../../../../../generated/swagger';
import { CallHistoryFilterType } from '../../../../../../samwebDescription/samweb/callHistory/callHistoryFilterType';
import { CallProgressState } from '../../../../../../samwebDescription/samweb/callHistory/callProgressState';
import { IBrickInstanceTargetingAction } from '../../../../../utility/IBrickInstanceTargetingAction';
import { CallHistoryBrowserSettingsState } from '../../../brick-settings/specific-brick-settings/call-history-browser-settings/call-history-browser-settings.state';
import { FilterItemsCleared } from '../../brick-filter-state/grid-filter.actions';
import { GridFilterState, IFilterSettings } from '../../brick-filter-state/grid-filter.state';
import {
   CallHistoryAllFilterCleared,
   CallHistoryBrowserMakeCall,
   CallHistoryChangeDataPage,
   CallHistoryChangeProgressState,
   CallHistoryClearAndStartLoadingEntries,
   CallHistoryEntrySelected,
   CallHistoryFiltersChanged,
   CallHistoryLoadMoreEntries,
   CallHistoryLoadNewestEntries,
   CallHistoryMoreEntriesLoaded,
   CallHistoryNewEntriesLoaded,
   CallHistoryProgressStateMemoChangedByServer,
   CallHistoryScrollTopChanged,
   CallHistorySearchChangedThroughIncomingCall,
   CallHistorySearchChangedThroughIncomingCallFinished,
   CallHistorySearchEnded,
   CallHistorySearchStarted,
   CallHistorySearchValueChanged,
   CallHistorySelectNextEntry,
   RemoveSpecifiedCallHistoryData,
} from './call-history-browser-behaviour.actions';

export interface CallHistoryBrowserBehaviourModel {
   behaviourInstances: { [brickInstanceId: string]: CallHistoryBrowserBehaviourInstance };
   progressState: { [uniqueCallId: string]: number };
   progressByUserState: { [uniqueCallId: string]: SamwinUserShortInfoRemoteDto };
   memo: { [uniqueCallId: string]: string };
   historyEntry: { [uniqueCallId: string]: CallHistoryEntryRemoteDto };
}

export interface CallHistoryBrowserBehaviourInstance {
   idOfLatestEntry: number;
   newDataAvailable: boolean;
   searchValue: string;
   searchValueForRetaining: string;
   loadedCallHistoryValues: CallHistoryResponseData;
   selectedEntryId: string;
   isSearching: boolean;
   scrollTop: number;
   dataPaging: {
      skip: number;
      take: number;
   };
}

export function defaultCallHistoryBehaviourInstance(): CallHistoryBrowserBehaviourInstance {
   return {
      idOfLatestEntry: null,
      newDataAvailable: false,
      loadedCallHistoryValues: { data: [], total: 0 },
      searchValue: null,
      searchValueForRetaining: null,
      selectedEntryId: null,
      isSearching: null,
      scrollTop: 0,
      dataPaging: {
         skip: 0,
         take: null,
      },
   };
}

@State<CallHistoryBrowserBehaviourModel>({
   name: 'callHistoryBrowserBehaviour',
   defaults: {
      behaviourInstances: {},
      progressByUserState: {},
      progressState: {},
      memo: {},
      historyEntry: {},
   },
})
@Injectable()
export class CallHistoryBrowserBehaviourState {
   constructor(private callHistoryService: CallHistoryService, private actions$: Actions, private store: Store) {
      this.actions$
         .pipe(
            ofActionCompleted(CallHistoryEntrySelected),
            tap((x) => this.store.dispatch(new SetUniqueCallIdForHistory(x?.action?.uniqueCallId)))
         )
         .subscribe();

      this.actions$
         .pipe(
            ofActionSuccessful(CallHistorySearchValueChanged, CallHistoryFiltersChanged, CallHistoryAllFilterCleared),
            groupBy((x: IBrickInstanceTargetingAction) => x.brickInstanceId),
            mergeMap((group) =>
               group.pipe(
                  debounceTime(450),
                  map((x) => this.store.dispatch(new CallHistoryClearAndStartLoadingEntries(x.brickInstanceId)))
               )
            )
         )
         .subscribe();

      this.actions$
         .pipe(
            ofActionCompleted(CallHistoryLoadMoreEntries),
            mergeMap((x: ActionCompletion<IBrickInstanceTargetingAction>) =>
               this.store
                  .select(CallHistoryBrowserBehaviourState.isSearching)
                  .pipe(
                     map((filterFn) => filterFn(x.action.brickInstanceId)),
                     distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
                     filter((isSearching) => !isSearching)
                  )
                  .pipe(
                     switchMap(() =>
                        interval(2500).pipe(
                           tap(() => this.store.dispatch(new CallHistoryLoadNewestEntries(x.action.brickInstanceId))),
                           takeUntil(CallHistoryBrowserBehaviourState.resetOnLoadingEntries$(this.actions$, x.action.brickInstanceId))
                        )
                     ),
                     takeUntil(CallHistoryBrowserBehaviourState.resetOnLoadingEntries$(this.actions$, x.action.brickInstanceId))
                  )
            )
         )
         .subscribe();
   }

   private static resetOnLoadingEntries$(actions$: Actions, brickInstanceId: string) {
      return merge(
         actions$.pipe(ofActionDispatched(StateResetAll)),
         actions$.pipe(
            ofActionDispatched(CallHistoryLoadMoreEntries),
            filter((y: IBrickInstanceTargetingAction) => y.brickInstanceId === brickInstanceId)
         )
      );
   }

   @Selector([GridFilterState.getBrickFilterConfiguration])
   public static getCallHistorySelectedFiltersFn(filterConfigFn) {
      return (brickInstanceId: string) => {
         const filterConfig: IFilterSettings = filterConfigFn(brickInstanceId, 'default', 'callHistoryFilter');
         return _.map(filterConfig.selectedValues, (x) => CallHistoryFilterType.valueByName(x));
      };
   }

   @Selector([CallHistoryBrowserBehaviourState])
   public static getSpecificBehaviourStateOrDefault(state: CallHistoryBrowserBehaviourModel) {
      return (brickInstanceId: string) => {
         return CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, brickInstanceId);
      };
   }

   @Selector([CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getSearchValueFn(specificBehaviourStateFn: (brickInstanceId: string) => CallHistoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return specificBehaviourStateFn(brickInstanceId).searchValue;
      };
   }

   @Selector([CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getCallHistoryValuesForBrick(callHistoryBrowserBehaviourForBrick: (brickInstanceId: string) => CallHistoryBrowserBehaviourInstance) {
      return (brickInstanceId: string): GridDataResult => {
         return <GridDataResult>callHistoryBrowserBehaviourForBrick(brickInstanceId).loadedCallHistoryValues;
      };
   }

   @Selector([CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getCallHistorySkipForBrick(callHistoryBrowserBehaviourForBrick: (brickInstanceId: string) => CallHistoryBrowserBehaviourInstance) {
      return (brickInstanceId: string): number => {
         return callHistoryBrowserBehaviourForBrick(brickInstanceId).dataPaging.skip;
      };
   }

   @Selector([CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getCallHistoryNewDataAvailableForBrick(callHistoryBrowserBehaviourForBrick: (brickInstanceId: string) => CallHistoryBrowserBehaviourInstance) {
      return (brickInstanceId: string): boolean => {
         return callHistoryBrowserBehaviourForBrick(brickInstanceId).newDataAvailable;
      };
   }

   @Selector([CallHistoryBrowserBehaviourState])
   public static getProgressStateForHistoryEntryFn(state: CallHistoryBrowserBehaviourModel) {
      return (uniqueCallId: string) => {
         const progressState = state.progressState[uniqueCallId];
         return CallProgressState.valueBySamwinValue(progressState);
      };
   }

   @Selector([CallHistoryBrowserBehaviourState])
   public static getProgressByUserForHistoryEntryFn(state: CallHistoryBrowserBehaviourModel) {
      return (uniqueCallId: string) => {
         return state.progressByUserState[uniqueCallId];
      };
   }

   @Selector([CallHistoryBrowserBehaviourState])
   public static getMemoForHistoryEntryFn(state: CallHistoryBrowserBehaviourModel) {
      return (uniqueCallId: string) => {
         return state.memo[uniqueCallId];
      };
   }

   @Selector([CallHistoryBrowserBehaviourState])
   public static getHistoryEntryFn(state: CallHistoryBrowserBehaviourModel) {
      return (uniqueCallId: string) => {
         return state.historyEntry[uniqueCallId];
      };
   }

   @Selector([CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getSelectedEntry(specificBehaviourStateFn: (brickInstanceId: string) => CallHistoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         const behaviourState = specificBehaviourStateFn(brickInstanceId);
         return behaviourState.loadedCallHistoryValues.data.find((entry) => entry.uniqueCallId === behaviourState.selectedEntryId);
      };
   }

   @Selector([CallHistoryBrowserBehaviourState.getSelectedEntry])
   public static getSelectedPeerId(getSelectedEntry: (brickInstanceId: string) => CallHistoryEntryRemoteDto) {
      return (brickInstanceId: string) => {
         return getSelectedEntry(brickInstanceId)?.peerId;
      };
   }

   @Selector([CallHistoryBrowserBehaviourState.getSelectedPeerId])
   public static isNumberSelected(selectedNumber) {
      return (brickInstanceId: string) => {
         const currentNumber = selectedNumber(brickInstanceId);
         if (_.isNil(currentNumber)) {
            return false;
         }
         return true;
      };
   }

   private static getSpecificBehaviourStateOrDefaultInternal(
      state: CallHistoryBrowserBehaviourModel,
      brickInstanceId: string
   ): CallHistoryBrowserBehaviourInstance {
      const value = state.behaviourInstances[brickInstanceId];
      if (value) {
         return value;
      } else {
         return defaultCallHistoryBehaviourInstance();
      }
   }

   @Selector([CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static getSelectedEntryId(callHistoryBrowserBehaviourForBrick: (brickInstanceId: string) => CallHistoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return callHistoryBrowserBehaviourForBrick(brickInstanceId).selectedEntryId;
      };
   }

   @Selector([CallHistoryBrowserBehaviourState.getSelectedEntry, CallHistoryBrowserBehaviourState.getCallHistoryValuesForBrick])
   public static getSelectedEntryIndexOnCurrentPage(
      getSelectedEntry: (brickInstanceId: string) => CallHistoryEntryRemoteDto,
      getCallHistoryValues: (brickInstanceId: string) => GridDataResult
   ) {
      return (brickInstanceId: string) => {
         const selectedEntry = getSelectedEntry(brickInstanceId);
         if (_.isNil(selectedEntry)) return -1;
         return getCallHistoryValues(brickInstanceId).data.findIndex((value) => value.uniqueCallId === selectedEntry.uniqueCallId);
      };
   }

   @Selector([CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault, CallHistoryBrowserBehaviourState.getSelectedEntryIndexOnCurrentPage])
   public static getSelectedEntryIndexOverAllPages(
      callHistoryBrowserBehaviourForBrick: (brickInstanceId: string) => CallHistoryBrowserBehaviourInstance,
      getSelectedEntryIndexOnCurrentPage: (brickInstanceId: string) => number
   ) {
      return (brickInstanceId: string) => {
         const indexOnPage = getSelectedEntryIndexOnCurrentPage(brickInstanceId);
         if (indexOnPage < 0) return -1;

         return callHistoryBrowserBehaviourForBrick(brickInstanceId).dataPaging.skip + indexOnPage;
      };
   }

   @Selector([CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefault])
   public static isSearching(callHistoryBrowserBehaviourForBrick: (brickInstanceId: string) => CallHistoryBrowserBehaviourInstance) {
      return (brickInstanceId: string) => {
         return callHistoryBrowserBehaviourForBrick(brickInstanceId).isSearching;
      };
   }

   @Selector([SessionState.getAuthenticatedUserId])
   public static getForeignCallHistoryEntries(authenticatedUserId: number) {
      return (entries: CallHistoryProgressMemoInfoRemoteDto[]) => {
         return entries.filter(
            (entry) => !_.isNil(entry.progressByUserInfo) && entry.progressByUserInfo?.id !== authenticatedUserId && entry.progressByUserInfo?.id !== 0
         );
      };
   }

   @Action(CallHistoryMoreEntriesLoaded)
   @ImmutableContext()
   public callHistoryMoreEntriesLoaded(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistoryMoreEntriesLoaded) {
      if (_.isEmpty(action.result.data)) {
         return;
      }
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         instance.loadedCallHistoryValues.data = action.result.data;
         // update the total count only when not loading more data during scrolling
         if (instance.dataPaging.skip === 0) {
            instance.loadedCallHistoryValues.total = action.result.total;
            instance.idOfLatestEntry = _(instance.loadedCallHistoryValues.data)
               .map((x) => x.id)
               .max();
         }
         state.behaviourInstances[action.brickInstanceId] = instance;

         action.result.data.forEach((updatedEntry) => {
            state.progressByUserState[updatedEntry.uniqueCallId] = updatedEntry.progressByUserInfo;
            state.progressState[updatedEntry.uniqueCallId] = updatedEntry.progressState;
            state.memo[updatedEntry.uniqueCallId] = updatedEntry.memo;
            state.historyEntry[updatedEntry.uniqueCallId] = updatedEntry;
         });

         return state;
      });
   }

   @Action(CallHistoryNewEntriesLoaded)
   @ImmutableContext()
   public callHistoryNewEntriesLoaded(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistoryNewEntriesLoaded) {
      if (_.isEmpty(action.result.data)) {
         return;
      }
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         // remember that new data is available (and do not update grid) when not scrolled to top
         if (instance.dataPaging.skip > 0 || instance.scrollTop > 0) {
            instance.newDataAvailable = true;
            return state;
         }

         let previousItems = instance.loadedCallHistoryValues.data;
         const selectedFilters = this.store.selectSnapshot(CallHistoryBrowserBehaviourState.getCallHistorySelectedFiltersFn)(action.brickInstanceId);
         if (selectedFilters.includes(CallHistoryFilterType.ONLY_NEWEST_CALLS_PER_PEER_ID)) {
            const loadedPeerIds = action.result.data.map((x) => x.peerId);
            previousItems = _.filter(previousItems, (x) => !loadedPeerIds.includes(x.peerId));
         }
         instance.loadedCallHistoryValues = {
            data: _.concat(action.result.data, previousItems).slice(0, instance.dataPaging.take),
            total: instance.loadedCallHistoryValues.total + action.result.total,
         };
         instance.idOfLatestEntry = _(instance.loadedCallHistoryValues.data)
            .map((x) => x.id)
            .max();
         state.behaviourInstances[action.brickInstanceId] = instance;

         action.result.data.forEach((updatedEntry) => {
            state.progressByUserState[updatedEntry.uniqueCallId] = updatedEntry.progressByUserInfo;
            state.progressState[updatedEntry.uniqueCallId] = updatedEntry.progressState;
            state.memo[updatedEntry.uniqueCallId] = updatedEntry.memo;
            state.historyEntry[updatedEntry.uniqueCallId] = updatedEntry;
         });

         return state;
      });
   }

   @Action(CallHistoryScrollTopChanged)
   @ImmutableContext()
   public callHistoryScrollTopChanged(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistoryScrollTopChanged) {
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         instance.scrollTop = action.scrollTop;
         state.behaviourInstances[action.brickInstanceId] = instance;
         return state;
      });
   }

   @Action(CallHistoryChangeDataPage)
   @ImmutableContext()
   public callHistoryChangeDataPage(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistoryChangeDataPage) {
      let changedSkip = false;
      let increasedTake = false;
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         const newSkip = action.skip ?? instance.dataPaging.skip;
         const newTake = action.take;
         changedSkip = instance.dataPaging.skip !== newSkip;
         increasedTake = instance.dataPaging.take < newTake;
         if (!changedSkip && !increasedTake) {
            instance.loadedCallHistoryValues.data = instance.loadedCallHistoryValues.data.slice(0, newTake);
         }
         instance.dataPaging.skip = newSkip;
         instance.dataPaging.take = newTake;
         instance.newDataAvailable = newSkip === 0 ? false : instance.newDataAvailable;
         state.behaviourInstances[action.brickInstanceId] = instance;
         return state;
      });

      if (!changedSkip && !increasedTake) {
         return;
      }

      return this.store.dispatch(new CallHistoryLoadMoreEntries(action.brickInstanceId));
   }

   @Action(CallHistoryClearAndStartLoadingEntries)
   @ImmutableContext()
   public callHistoryClearAndStartLoadingEntries(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistoryClearAndStartLoadingEntries) {
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         instance.newDataAvailable = false;
         instance.idOfLatestEntry = null;
         instance.dataPaging.skip = 0;
         // avoid loading entire history if take parameter is not set (should not happen)
         if (_.isNil(instance.dataPaging.take)) {
            console.warn('Avoided loading entire Call History - check CallHistoryClearAndStartLoadingEntries trigger');
            instance.dataPaging.take = 1;
         }
         instance.loadedCallHistoryValues = { data: [], total: 0 };
         state.behaviourInstances[action.brickInstanceId] = instance;
         return state;
      });
      return ctx.dispatch(new CallHistoryLoadMoreEntries(action.brickInstanceId));
   }

   @Action(CallHistoryLoadMoreEntries)
   public callHistoryLoadMoreEntries(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistoryLoadMoreEntries) {
      const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(ctx.getState(), action.brickInstanceId);

      return this.callHistoryService
         .getCallHistory({
            fromFilter: null,
            filter: this.createCallHistoryFilter(instance.dataPaging.skip, instance.dataPaging.take, action.brickInstanceId, instance),
         })
         .pipe(
            tap((response) => {
               // Select first entry of Grid after values are loaded.
               const row = _(response.data).head();
               if (!_.isNil(row) && instance.selectedEntryId === null) {
                  ctx.dispatch(new CallHistoryEntrySelected(action.brickInstanceId, row.uniqueCallId));
               }
            }),
            mergeMap((x) => ctx.dispatch(new CallHistoryMoreEntriesLoaded(action.brickInstanceId, x))),
            takeUntil(
               this.actions$.pipe(
                  ofActionDispatched(CallHistoryLoadMoreEntries),
                  filter((x: CallHistoryLoadMoreEntries) => x.brickInstanceId === action.brickInstanceId)
               )
            )
         )
         .pipe(
            tap(() => {
               ctx.dispatch(new CallHistorySearchEnded(action.brickInstanceId));
            })
         );
   }

   @Action(CallHistoryLoadNewestEntries)
   public callHistoryLoadNewestEntries(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistoryLoadNewestEntries) {
      const state = ctx.getState();
      const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);

      // no need to poll for new data when we already know there is new data
      if (instance.newDataAvailable) return;

      const currentProgressMemoStateInfos: CallHistoryProgressMemoInfoRemoteDto[] = _(state.progressState)
         .mapValues(
            (value, key) =>
               <CallHistoryProgressMemoInfoRemoteDto>{
                  uniqueCallId: key,
                  progressState: value,
                  progressByUserId: state.progressByUserState[key]?.id,
                  memo: state.memo[key],
               }
         )
         .values()
         .value();

      return this.callHistoryService
         .getCallHistory({
            untilFilter: null,
            historyFromEntryWithId: instance.idOfLatestEntry,
            filter: this.createCallHistoryFilter(0, null, action.brickInstanceId, instance),
            optionalCurrentCallProcessStateInfos: currentProgressMemoStateInfos,
         })
         .pipe(
            mergeMap((responseData) => ctx.dispatch(new CallHistoryNewEntriesLoaded(action.brickInstanceId, responseData))),
            takeUntil(
               this.actions$.pipe(
                  ofActionDispatched(CallHistoryLoadNewestEntries, CallHistoryLoadMoreEntries),
                  filter((x: CallHistoryLoadNewestEntries) => x.brickInstanceId === action.brickInstanceId)
               )
            )
         );
   }

   private createCallHistoryFilter(
      skip: number,
      count: number,
      brickInstanceId: string,
      instance: CallHistoryBrowserBehaviourInstance
   ): CallHistoryFilterRemoteDto {
      const selectedFilters = this.store.selectSnapshot(CallHistoryBrowserBehaviourState.getCallHistorySelectedFiltersFn)(brickInstanceId);

      return <CallHistoryFilterRemoteDto>{
         pagingInfo: {
            skip: skip,
            take: count,
         },
         searchText: instance.searchValue,
         filterForInboundCalls: selectedFilters.includes(CallHistoryFilterType.INBOUND),
         filterForOutboundCalls: selectedFilters.includes(CallHistoryFilterType.OUTBOUND),
         filterForInternalCalls: selectedFilters.includes(CallHistoryFilterType.INTERNAL),
         filterForExternalCalls: selectedFilters.includes(CallHistoryFilterType.EXTERNAL),
         filterForMissedOrNoAnswerCalls: selectedFilters.includes(CallHistoryFilterType.MISSED_OR_NO_ANSWER),
         filterForTransferredCalls: selectedFilters.includes(CallHistoryFilterType.TRANSFERRED),
         filterForDeclinedCalls: selectedFilters.includes(CallHistoryFilterType.DECLINED),
         filterForTodoCalls: selectedFilters.includes(CallHistoryFilterType.TODO),
         filterForInProgressCalls: selectedFilters.includes(CallHistoryFilterType.IN_PROGRESS),
         filterForDoneCalls: selectedFilters.includes(CallHistoryFilterType.DONE),
         filterForOnlyActiveSkills: selectedFilters.includes(CallHistoryFilterType.ONLY_ACTIVE_SKILLS),
         onlyCalls: selectedFilters.includes(CallHistoryFilterType.ONLY_MY_CALLS),
         onlyChats: selectedFilters.includes(CallHistoryFilterType.ONLY_MY_CHATS),
         onlyMyConversations: selectedFilters.includes(CallHistoryFilterType.ONLY_MY_CONVERSATIONS),
         justNewestCallsPerPeerId: selectedFilters.includes(CallHistoryFilterType.ONLY_NEWEST_CALLS_PER_PEER_ID),
      };
   }

   @Action(CallHistorySearchValueChanged)
   @ImmutableContext()
   public callHistorySearchValueChanged(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistorySearchValueChanged) {
      ctx.dispatch(new CallHistorySearchStarted(action.brickInstanceId));
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         instance.searchValue = action.searchValue;
         instance.searchValueForRetaining = null;
         state.behaviourInstances[action.brickInstanceId] = instance;
         return state;
      });
   }

   @Action(CallHistoryAllFilterCleared)
   @ImmutableContext()
   public callHistoryAllFilterCleared(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistoryAllFilterCleared) {
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         instance.searchValue = null;
         state.behaviourInstances[action.brickInstanceId] = instance;
         return state;
      });
      return this.store.dispatch(new FilterItemsCleared(action.brickInstanceId, 'default'));
   }

   @Action(CallHistorySearchChangedThroughIncomingCall)
   @ImmutableContext()
   public callHistorySearchChangedThroughIncomingCall(
      ctx: StateContext<CallHistoryBrowserBehaviourModel>,
      action: CallHistorySearchChangedThroughIncomingCall
   ) {
      const isRetainFilterOnCallFinish = this.store.selectSnapshot(CallHistoryBrowserSettingsState.getRetainFilterOnCallFinish)(action.brickInstanceId);
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         instance.searchValue = action.searchValue;
         if (isRetainFilterOnCallFinish) {
            instance.searchValueForRetaining = instance.searchValue;
         }
         state.behaviourInstances[action.brickInstanceId] = instance;
         return state;
      });

      return this.store.dispatch(new CallHistoryClearAndStartLoadingEntries(action.brickInstanceId));
   }

   @Action(CallHistorySearchChangedThroughIncomingCallFinished)
   @ImmutableContext()
   public callHistorySearchChangedThroughIncomingCallFinished(
      ctx: StateContext<CallHistoryBrowserBehaviourModel>,
      action: CallHistorySearchChangedThroughIncomingCallFinished
   ) {
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         instance.searchValue = instance.searchValueForRetaining;
         instance.searchValueForRetaining = null;
         state.behaviourInstances[action.brickInstanceId] = instance;
         return state;
      });

      if (!this.store.selectSnapshot(CallHistoryBrowserSettingsState.getRetainFilterOnCallFinish)(action.brickInstanceId)) {
         return this.store.dispatch(new CallHistoryClearAndStartLoadingEntries(action.brickInstanceId));
      }
   }

   @Action(CallHistoryEntrySelected)
   @ImmutableContext()
   public callHistoryEntrySelected(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistoryEntrySelected) {
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         instance.selectedEntryId = action.uniqueCallId;
         state.behaviourInstances[action.brickInstanceId] = instance;
         return state;
      });

      const selectedNumber = this.store.selectSnapshot(CallHistoryBrowserBehaviourState.getSelectedPeerId)(action.brickInstanceId);
      if (_.isNil(selectedNumber) || _.isUndefined(selectedNumber)) {
         return;
      }
   }

   @Action(CallHistorySelectNextEntry)
   @ImmutableContext()
   public callHistorySelectNextEntry(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistorySelectNextEntry) {
      const callHistoryState = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(ctx.getState(), action.brickInstanceId);
      const currentValues = _(callHistoryState.loadedCallHistoryValues.data)
         .orderBy((y) => y.timestamp, 'desc')
         .value();
      let currentIndex = _(currentValues).findIndex((value) => value.uniqueCallId === callHistoryState.selectedEntryId);
      if (_.isNil(currentIndex)) {
         currentIndex = -1;
      }
      const lastIndex = _(currentValues).findLastIndex();
      let nextIndex = currentIndex + (action.direction === 'up' ? -action.step : action.step);
      if (nextIndex > lastIndex) {
         nextIndex = lastIndex;
      }
      if (nextIndex < 0) {
         nextIndex = 0;
      }
      const selectedCallHistory = currentValues[nextIndex]?.uniqueCallId;
      if (_.isNil(selectedCallHistory)) {
         return;
      }
      return ctx.dispatch(new CallHistoryEntrySelected(action.brickInstanceId, selectedCallHistory));
   }

   @Action(CallHistoryChangeProgressState)
   public callHistoryChangeProgressState(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistoryChangeProgressState) {
      return this.callHistoryService.changeCallHistoryProgressState({
         uniqueCallId: action.uniqueCallId,
         progressState: action.progressState.samwinValue,
         originalAgentId: action.originalAgentId,
      });
   }

   @Action(CallHistoryProgressStateMemoChangedByServer)
   @ImmutableContext()
   public callHistoryProgressStateMemoChangedByServer(
      ctx: StateContext<CallHistoryBrowserBehaviourModel>,
      action: CallHistoryProgressStateMemoChangedByServer
   ) {
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         action.data.changedEntries.forEach((updatedEntry) => {
            state.progressByUserState[updatedEntry.uniqueCallId] = updatedEntry.progressByUserInfo;
            state.progressState[updatedEntry.uniqueCallId] = updatedEntry.progressState;
            state.memo[updatedEntry.uniqueCallId] = updatedEntry.memo;
            state.historyEntry[updatedEntry.uniqueCallId].memo = updatedEntry.memo;
            state.historyEntry[updatedEntry.uniqueCallId].progressState = updatedEntry.progressState;
            state.historyEntry[updatedEntry.uniqueCallId].progressByUserInfo = updatedEntry.progressByUserInfo;
         });
         return state;
      });
   }

   @Action(CallHistorySearchStarted)
   @ImmutableContext()
   public callHistorySearchStarted(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistorySearchStarted) {
      ctx.dispatch(new CallHistoryEntrySelected(action.brickInstanceId, null));
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         instance.isSearching = true;
         state.behaviourInstances[action.brickInstanceId] = instance;
         return state;
      });
   }

   @Action(CallHistorySearchEnded)
   @ImmutableContext()
   public callHistorySearchEnded(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistorySearchEnded) {
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
         instance.isSearching = false;
         state.behaviourInstances[action.brickInstanceId] = instance;
         return state;
      });
   }

   @Action(CallHistoryBrowserMakeCall)
   @ImmutableContext()
   public callHistoryBrowserMakeCall(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: CallHistoryBrowserMakeCall) {
      const state = ctx.getState();
      const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);
      const selectedNumber$ = this.store
         .select(CallHistoryBrowserBehaviourState.getSelectedPeerId)
         .pipe(map((getSelectedNumber) => getSelectedNumber(action.brickInstanceId)));
      const isSearching$ = this.store.select(CallHistoryBrowserBehaviourState.isSearching).pipe(map((isSearching) => isSearching(action.brickInstanceId)));

      const callEntry$ = isSearching$
         .pipe(
            switchMap((isSearching) =>
               selectedNumber$.pipe(
                  switchMap((selectedNumber) => {
                     if (!isSearching) {
                        if (!_.isNil(selectedNumber)) {
                           return ctx.dispatch(new StartNewCallAction(selectedNumber));
                        }
                        // check if value isNumeric
                        if (!isNaN(+instance.searchValue)) {
                           return ctx.dispatch(new StartNewCallAction(instance.searchValue));
                        }
                     }
                     return of(null);
                  }),
                  map(() => isSearching)
               )
            )
         )
         .pipe(takewhile((isSearching) => isSearching));

      return callEntry$;
   }

   @Action(RemoveSpecifiedCallHistoryData)
   @ImmutableContext()
   public removeSpecifiedCallHistoryData(ctx: StateContext<CallHistoryBrowserBehaviourModel>, action: RemoveSpecifiedCallHistoryData) {
      ctx.setState((state: CallHistoryBrowserBehaviourModel) => {
         const instance = CallHistoryBrowserBehaviourState.getSpecificBehaviourStateOrDefaultInternal(state, action.brickInstanceId);

         const uniqueCallIdsToRemove = action.dataToRemove.map((entry) => entry.uniqueCallId);

         instance.loadedCallHistoryValues.data = instance.loadedCallHistoryValues.data.filter((entry) => !uniqueCallIdsToRemove.includes(entry.uniqueCallId));

         state.behaviourInstances[action.brickInstanceId] = instance;

         return state;
      });
   }
}
